import { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { getTwilioTokenGQL, setPresenceGQL, clearPresenceGQL } from '../../../../apollo';

const HEARTBEAT_MS = 4 * 60 * 1000;

export default function useTwilioDevice(onIncoming) {
  const [status, setStatus] = useState('offline');
  const [error, setError] = useState(null);
  const deviceRef = useRef(null);
  const heartbeatRef = useRef(null);
  const onIncomingRef = useRef(onIncoming);
  onIncomingRef.current = onIncoming;
  const [setPresence] = useMutation(setPresenceGQL);
  const [clearPresence] = useMutation(clearPresenceGQL);

  const [fetchToken] = useLazyQuery(getTwilioTokenGQL, {
    fetchPolicy: 'network-only',
    onError: (e) => setError(e),
    onCompleted: ({ getTwilioToken: token }) => {
      if (!token) return;
      if (deviceRef.current) {
        deviceRef.current.updateToken(token); // refresh path
        return;
      }
      const device = new Device(token, { closeProtection: true });
      deviceRef.current = device;
      device.on('registered', () => {
        setStatus('registered');
        setPresence({ variables: { online: true } });
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(
          () => setPresence({ variables: { online: true } }), HEARTBEAT_MS,
        );
      });
      device.on('unregistered', () => setStatus('offline'));
      device.on('error', (e) => setError(e));
      device.on('incoming', (call) => onIncomingRef.current(call));
      device.on('tokenWillExpire', () => fetchToken());
      setStatus('registering');
      device.register().catch((e) => setError(e));
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    fetchToken();
    const teardown = () => { clearPresence(); };
    window.addEventListener('beforeunload', teardown);
    return () => {
      window.removeEventListener('beforeunload', teardown);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      clearPresence();
      if (deviceRef.current) deviceRef.current.destroy();
      deviceRef.current = null;
    };
  }, []);

  return { device: deviceRef.current, status, error };
}
